import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    Plus, 
    Search, 
    Layers,
    Eye,
    Clock,
    CheckCircle,
    PlayCircle,
    Trash2,
    AlertTriangle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const initialForm = {
    numero_lote: '',
    marca: '',
    modelo: '',
    descripcion: '',
    observaciones: ''
};

export const Lotes = () => {
    const [lotes, setLotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [historialModalOpen, setHistorialModalOpen] = useState(false);
    const [form, setForm] = useState(initialForm);
    const [selectedLote, setSelectedLote] = useState(null);
    const [historial, setHistorial] = useState([]);
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    useEffect(() => {
        fetchLotes();
    }, []);

    const fetchLotes = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/lotes`);
            setLotes(response.data);
        } catch (error) {
            console.error('Error:', error);
            toast.error('Error al cargar lotes');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.numero_lote || !form.marca) {
            toast.error('Complete los campos requeridos');
            return;
        }

        try {
            await axios.post(`${API_URL}/api/lotes`, form);
            toast.success('Lote creado exitosamente');
            setModalOpen(false);
            setForm(initialForm);
            fetchLotes();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al crear lote');
        }
    };

    const handleDelete = async (loteId) => {
        try {
            await axios.delete(`${API_URL}/api/lotes/${loteId}`);
            toast.success('Lote eliminado');
            setDeleteConfirm(null);
            fetchLotes();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al eliminar lote');
            setDeleteConfirm(null);
        }
    };

    const verHistorial = async (lote) => {
        setSelectedLote(lote);
        try {
            const response = await axios.get(`${API_URL}/api/lotes/${lote.id}/historial`);
            setHistorial(response.data);
            setHistorialModalOpen(true);
        } catch (error) {
            toast.error('Error al cargar historial');
        }
    };

    const cambiarEstado = async (loteId, estado) => {
        try {
            await axios.put(`${API_URL}/api/lotes/${loteId}/estado?estado=${estado}`);
            toast.success('Estado actualizado');
            fetchLotes();
        } catch (error) {
            toast.error('Error al actualizar estado');
        }
    };

    const filteredLotes = lotes.filter(l =>
        l.numero_lote.toLowerCase().includes(searchTerm.toLowerCase()) ||
        l.marca.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getEstadoIcon = (estado) => {
        switch (estado) {
            case 'terminado': return <CheckCircle className="w-4 h-4 text-emerald-400" />;
            case 'en_proceso': return <PlayCircle className="w-4 h-4 text-yellow-400" />;
            default: return <Clock className="w-4 h-4 text-amber-400" />;
        }
    };

    const getEstadoClass = (estado) => {
        switch (estado) {
            case 'terminado': return 'badge-success';
            case 'en_proceso': return 'badge-warning';
            default: return 'badge-info';
        }
    };

    const getHistorialIcon = (tipo) => {
        const icons = {
            tela: '🧵',
            corte: '✂️',
            maquila: '🏭',
            lavanderia: '🧺',
            habilitacion: '📦'
        };
        return icons[tipo] || '📋';
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fadeIn" data-testid="lotes-page">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-zinc-100" >
                        Gestión de Lotes
                    </h1>
                    <p className="text-zinc-400 mt-1">Control y trazabilidad de lotes de producción</p>
                </div>
                <Button
                    onClick={() => {
                        setForm(initialForm);
                        setModalOpen(true);
                    }}
                    className="bg-amber-400 hover:bg-amber-500 text-zinc-900"
                    data-testid="add-lote-btn"
                >
                    <Plus className="w-5 h-5 mr-2" />
                    Nuevo Lote
                </Button>
            </div>

            <Card className="card">
                <CardContent className="p-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                        <Input
                            placeholder="Buscar por número de lote o marca..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10"
                            data-testid="search-lotes"
                        />
                    </div>
                </CardContent>
            </Card>

            <Card className="card">
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-zinc-800">
                                    <TableHead className="table-header">Nº Lote</TableHead>
                                    <TableHead className="table-header">Marca</TableHead>
                                    <TableHead className="table-header">Modelo</TableHead>
                                    <TableHead className="table-header">Estado</TableHead>
                                    <TableHead className="table-header">Fecha</TableHead>
                                    <TableHead className="table-header text-right">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredLotes.length > 0 ? (
                                    filteredLotes.map((lote) => (
                                        <TableRow key={lote.id} className="table-row" data-testid={`lote-row-${lote.id}`}>
                                            <TableCell className="table-cell font-medium">
                                                <div className="flex items-center gap-2">
                                                    <Layers className="w-4 h-4 text-amber-400" />
                                                    {lote.numero_lote}
                                                </div>
                                            </TableCell>
                                            <TableCell className="table-cell">{lote.marca}</TableCell>
                                            <TableCell className="table-cell">{lote.modelo}</TableCell>
                                            <TableCell className="table-cell">
                                                <span className={`${getEstadoClass(lote.estado)} flex items-center gap-1 w-fit`}>
                                                    {getEstadoIcon(lote.estado)}
                                                    {lote.estado === 'activo' ? 'Activo' : lote.estado === 'en_proceso' ? 'En Proceso' : 'Terminado'}
                                                </span>
                                            </TableCell>
                                            <TableCell className="table-cell">{lote.created_at?.split('T')[0]}</TableCell>
                                            <TableCell className="table-cell text-right">
                                                <div className="flex justify-end gap-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => verHistorial(lote)}
                                                        data-testid={`ver-historial-${lote.id}`}
                                                    >
                                                        <Eye className="w-4 h-4 text-amber-400" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => setDeleteConfirm(lote)}
                                                        data-testid={`delete-lote-${lote.id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4 text-red-400" />
                                                    </Button>
                                                    {lote.estado === 'activo' && (
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => cambiarEstado(lote.id, 'en_proceso')}
                                                        >
                                                            Iniciar
                                                        </Button>
                                                    )}
                                                    {lote.estado === 'en_proceso' && (
                                                        <Button
                                                            size="sm"
                                                            className="bg-emerald-500 hover:bg-emerald-600"
                                                            onClick={() => cambiarEstado(lote.id, 'terminado')}
                                                        >
                                                            Terminar
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-8 text-zinc-400">
                                            No hay lotes registrados
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Create Modal */}
            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent className="sm:max-w-[500px]" data-testid="lote-modal">
                    <DialogHeader>
                        <DialogTitle >Nuevo Lote</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="form-group">
                            <Label className="form-label">Número de Lote *</Label>
                            <Input
                                value={form.numero_lote}
                                onChange={(e) => setForm({ ...form, numero_lote: e.target.value })}
                                placeholder="Ej: LOTE-2024-001"
                                data-testid="input-numero-lote"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group">
                                <Label className="form-label">Marca *</Label>
                                <Input
                                    value={form.marca}
                                    onChange={(e) => setForm({ ...form, marca: e.target.value })}
                                    placeholder="Marca"
                                    data-testid="input-marca"
                                />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Modelo</Label>
                                <Input
                                    value={form.modelo}
                                    onChange={(e) => setForm({ ...form, modelo: e.target.value })}
                                    placeholder="Modelo"
                                    data-testid="input-modelo"
                                />
                            </div>
                        </div>
                        <div className="form-group">
                            <Label className="form-label">Descripción</Label>
                            <Textarea
                                value={form.descripcion}
                                onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                                placeholder="Descripción del lote"
                                rows={2}
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                                Cancelar
                            </Button>
                            <Button type="submit" className="bg-amber-400 hover:bg-amber-500 text-zinc-900" data-testid="save-lote-btn">
                                Crear Lote
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Historial Modal */}
            <Dialog open={historialModalOpen} onOpenChange={setHistorialModalOpen}>
                <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto" data-testid="historial-modal">
                    <DialogHeader>
                        <DialogTitle >
                            Historial del Lote: {selectedLote?.numero_lote}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        {historial.length > 0 ? (
                            <div className="relative">
                                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-zinc-700"></div>
                                {historial.map((item, index) => (
                                    <div key={index} className="relative pl-10 pb-6">
                                        <div className="absolute left-2 w-5 h-5 rounded-full bg-amber-400 flex items-center justify-center text-xs">
                                            {getHistorialIcon(item.tipo)}
                                        </div>
                                        <div className="bg-zinc-800 rounded-lg p-4">
                                            <div className="flex justify-between items-start mb-2">
                                                <span className="font-medium text-zinc-100 capitalize">
                                                    {item.tipo} - {item.subtipo}
                                                </span>
                                                <span className="text-sm text-zinc-400">
                                                    {item.fecha} {item.hora}
                                                </span>
                                            </div>
                                            <div className="text-sm text-zinc-400">
                                                {item.tipo === 'tela' && (
                                                    <p>{item.data.cantidad_metros}m de {item.data.tipo_tela} {item.data.color}</p>
                                                )}
                                                {item.tipo === 'corte' && (
                                                    <p>{item.data.cantidad_total} piezas por {item.data.cortador_nombre}</p>
                                                )}
                                                {item.tipo === 'maquila' && (
                                                    <p>{item.data.cantidad_entregada} piezas - {item.data.maquilador_nombre}</p>
                                                )}
                                                {item.tipo === 'lavanderia' && (
                                                    <p>{item.data.cantidad_enviada} piezas</p>
                                                )}
                                                {item.tipo === 'habilitacion' && (
                                                    <p>{item.data.cantidad} piezas habilitadas</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-zinc-400">
                                No hay movimientos registrados para este lote
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
                <DialogContent className="sm:max-w-[400px]" data-testid="delete-lote-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-400">
                            <AlertTriangle className="w-5 h-5" /> Eliminar Lote
                        </DialogTitle>
                    </DialogHeader>
                    <p className="text-zinc-300">Esta seguro de eliminar el lote <strong>{deleteConfirm?.numero_lote}</strong>?</p>
                    <p className="text-xs text-zinc-500">Esta accion no se puede deshacer. No se podra eliminar si tiene cortes asociados.</p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
                        <Button className="bg-red-500 hover:bg-red-600" onClick={() => handleDelete(deleteConfirm.id)} data-testid="confirm-delete-lote">Eliminar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
